import React from 'react';
import { changeId, diffCsv, type DiffResult } from './model/diff';
import { parseCsv, parseForSchema, type ParsedCsv } from './model/parse';
import { sameLabel, type CsvSchema } from './model/schema';
import { defaultSelection, selectNewlySelectable } from './model/selection';
import { validateCsv, type ValidationResult } from './model/validate';

/** How long the list must stay still before an open review is checked against it again. */
const LIST_SETTLED_MS = 200;

type Review = { validation?: ValidationResult; diff?: DiffResult };

const NO_REVIEW: Review = { validation: undefined, diff: undefined };

/**
 * State of one import: file, corrections, review, selection and confirmation. The review
 * stays current as cells are edited, as the list changes underneath it, and as the
 * schema changes.
 */
export const useCsvReview = ({
  schema,
  currentData,
  removalByDefault,
  addOnly,
  committing
}: {
  schema: CsvSchema;
  currentData: Record<string, string>[];
  removalByDefault: boolean;
  addOnly: boolean;
  /** While a write runs, changes to the list are its own and not re-checked. */
  committing: boolean;
}) => {
  const [filename, setFilename] = React.useState<string>();
  const [reading, setReading] = React.useState<string>();
  const [parsed, setParsed] = React.useState<ParsedCsv>();
  // Corrections by line and field. A ref, not state: typing must not redraw the grid.
  const edits = React.useRef<Record<number, Record<string, string>>>({});
  const [checked, setChecked] = React.useState<Review>(NO_REVIEW);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [confirming, setConfirming] = React.useState(false);
  /** Whether confirming was interrupted by the list changing underneath it. */
  const [listMoved, setListMoved] = React.useState(false);
  const [treatAbsentAsRemoval, setTreatAbsentAsRemoval] = React.useState(removalByDefault);
  const { validation, diff } = checked;

  const review = React.useCallback((): Review => {
    if (!parsed) {
      return NO_REVIEW;
    }
    // A field the file has no column for gets one once a row is edited there, or the edit
    // would have nowhere to go.
    const editedKeys = new Set(Object.values(edits.current).flatMap(Object.keys));
    const columnsForEdits = schema.fields
      .filter(
        (field) =>
          editedKeys.has(field.key) && !parsed.header.some((label) => sameLabel(label, field.label))
      )
      .map((field) => field.label);
    const header = [...parsed.header, ...columnsForEdits];
    const headerFields = header.map((label) =>
      schema.fields.find((candidate) => sameLabel(candidate.label, label))
    );
    const withEdits = (line: number, cells: string[]) => {
      const edit = edits.current[line] ?? {};
      return headerFields.map((field, index) => {
        if (field && field.key in edit) {
          return edit[field.key];
        }
        // A cell past the file's own header is stray data, not a value for an added column.
        const isFileColumn = index < parsed.header.length;
        return isFileColumn ? (cells[index] ?? '') : '';
      });
    };
    const result = validateCsv(schema, {
      header,
      rows: parsed.rows.map((row) => ({ ...row, cells: withEdits(row.line, row.cells) }))
    });
    return {
      validation: result,
      diff: diffCsv(schema, result, currentData, { treatAbsentAsRemoval, addOnly })
    };
  }, [parsed, schema, currentData, treatAbsentAsRemoval, addOnly]);

  /** Checks the file again, selecting whatever has just become something to decide. */
  const recheck = React.useCallback(() => {
    const next = review();
    setChecked(next);
    setSelected((current) => selectNewlySelectable(current, diff, next.diff));
    return next;
  }, [review, diff]);

  const load = (name: string, text: string) => {
    const read = parseForSchema(schema, text);
    setFilename(name);
    setParsed(read);
    setSelected(
      defaultSelection(
        diffCsv(schema, validateCsv(schema, read), currentData, {
          treatAbsentAsRemoval,
          addOnly
        })
      )
    );
  };

  /** A file refused on its header alone, shown with the same problem a full read would. */
  const loadHeader = (name: string, head: string) => {
    setFilename(name);
    setParsed(parseCsv(head));
  };

  const recordEdit = (line: number, key: string, value: string) => {
    edits.current = { ...edits.current, [line]: { ...edits.current[line], [key]: value } };
    setConfirming(false);
  };

  /** Import: check the file once more and ask the user to confirm what it would do. */
  const startConfirming = () => {
    setChecked(review());
    setConfirming(true);
    setListMoved(false);
  };

  const reset = () => {
    setReading(undefined);
    setFilename(undefined);
    setParsed(undefined);
    // Cleared together, or the grid flashes with every row struck through.
    setChecked(NO_REVIEW);
    edits.current = {};
    setSelected(new Set());
    setTreatAbsentAsRemoval(removalByDefault);
    setListMoved(false);
    setConfirming(false);
  };

  const uploaded = React.useRef<ParsedCsv>();
  React.useEffect(() => {
    if (parsed !== uploaded.current) {
      uploaded.current = parsed;
      setChecked(review());
    }
    // review() closes over the edits, which must not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed]);

  React.useEffect(() => {
    if (validation) {
      setReading(undefined);
    }
  }, [validation]);

  // Deletions come and go with the whole-list choice, each arriving selected.
  const answeredFor = React.useRef(treatAbsentAsRemoval);
  React.useEffect(() => {
    if (answeredFor.current === treatAbsentAsRemoval) {
      return;
    }
    answeredFor.current = treatAbsentAsRemoval;
    const next = review();
    setChecked(next);
    setConfirming(false);
    setSelected((current) => {
      const kept = [...current].filter((id) => !id.startsWith('removed:'));
      const removals = (next.diff?.changes ?? [])
        .filter((change) => change.classification === 'removed')
        .map(changeId);
      return new Set([...kept, ...removals]);
    });
    // review() closes over the edits, which must not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatAbsentAsRemoval]);

  // Re-check once the list settles; a change while confirming sends the user back. State
  // is read when the wait ends, since Confirm changes the list and then clears the file.
  const currentKey = React.useMemo(() => JSON.stringify(currentData), [currentData]);
  const readAgainst = React.useRef(currentKey);
  const latest = React.useRef({ parsed, confirming, committing, recheck });
  latest.current = { parsed, confirming, committing, recheck };
  React.useEffect(() => {
    if (readAgainst.current === currentKey) {
      return;
    }
    const timer = setTimeout(() => {
      readAgainst.current = currentKey;
      const now = latest.current;
      if (!now.parsed || now.committing) {
        return;
      }
      now.recheck();
      if (now.confirming) {
        setConfirming(false);
        setListMoved(true);
      }
    }, LIST_SETTLED_MS);
    return () => clearTimeout(timer);
  }, [currentKey]);

  // A consumer's schema can follow its page, such as options the page holds or a setting a
  // column's check reads, so a new one checks the open file again and leaves confirming.
  const checkedWith = React.useRef(schema);
  React.useEffect(() => {
    if (checkedWith.current === schema) {
      return;
    }
    checkedWith.current = schema;
    const now = latest.current;
    if (now.parsed && !now.committing) {
      now.recheck();
      setConfirming(false);
    }
  }, [schema]);

  return {
    filename,
    reading,
    setReading,
    validation,
    diff,
    selected,
    setSelected,
    confirming,
    setConfirming,
    listMoved,
    treatAbsentAsRemoval,
    setTreatAbsentAsRemoval,
    load,
    loadHeader,
    recordEdit,
    recheck,
    startConfirming,
    reset
  };
};
